import React from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';

import { Columns, Column, MuiTitle } from 'src/ui/mui-ui-kit';;
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import {
  setBreadcrumbs,
  reduceServerErrors,
  ConfirmDeletionModal,
  UiToastAdapter,
  GuidelinesCard,
} from 'src/ui';

import { getToken } from 'src/auth';
import { getUser } from 'src/user';
import { getAccount, fetchAccount, deleteAccountLocation, updateAccountLocation } from 'src/account';
import { LocationsTable } from '../components';
import { LocationNotesModal } from '../components/LocationNotesModal';

const guidelines = [
  `Consider each location you do business in, including corporate headquarters and retail or production locations, and be sure to list them here. Don't forget to include mailing locations.`,
  `Please include an image of any licensing pertaining to a specific location, for example a business license for your headquarters location or any specialty licenses such as for a dispensary or grow operation.`,
];

const guidelinesPayments = [
  `Please add location information about your business. A primary address is required.`,
  `If your doing business at address is different then your primary address, please add an additional address using the Retail Option from the drop down menu`,
];

const LocationsListPage = ({
  setBreadcrumbs,
  fetchAccount,
  token,
  user,
  account,
  deleteAccountLocation,
  updateAccountLocation,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [deletingLocation, setDeletingLocation] = React.useState(null);
  const [notesModalOpen, setNotesModalOpen] = React.useState(false);
  const [selectedLocationForNotes, setSelectedLocationForNotes] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      setBreadcrumbs([{ label: 'Locations', href: '/locations' }]);

      if (token.isValid()) {
        if (user.accountId) {
          await fetchAccount(user.accountId);

          if (account.isError()) {
            reduceServerErrors(account);
          }
        }
      }
      setLoading(false);
    })();
  }, []);

  const onDeleteLocation = location => {
    setDeletingLocation(location);
  };

  const onCancelDeleteLocation = () => {
    setDeletingLocation(null);
  };

  const onConfirmDeleteLocation = async () => {
    setLoading(true);

    onCancelDeleteLocation();

    await deleteAccountLocation({
      accountId: user.accountId,
      locationId: deletingLocation.id,
    });

    setLoading(false);

    if (account.isError()) {
      return reduceServerErrors(account);
    }
    toast(UiToastAdapter('Location deleted successfully!', 'success'));
    return undefined;
  };

  const onViewNotes = (location) => {
    setSelectedLocationForNotes(location);
    setNotesModalOpen(true);
  };

  const onCloseNotesModal = () => {
    setNotesModalOpen(false);
    // Use setTimeout to ensure state updates properly
    setTimeout(() => {
      setSelectedLocationForNotes(null);
    }, 0);
  };

  const onSubmitNotes = async (values) => {
    // Store the location ID before any potential state changes
    const locationId = selectedLocationForNotes?.id;

    if (!locationId) {
      console.error('No location selected for notes update');
      toast(UiToastAdapter('Error: No location selected', 'error'));
      return;
    }

    try {
      // Don't close modal yet - let the loading spinner show
      await updateAccountLocation({
        location_notes: values.notes,
        accountId: user.accountId,
        locationId: locationId,
      });

      if (account.isError()) {
        toast(UiToastAdapter('Error updating location notes', 'error'));
        onCloseNotesModal();
        return reduceServerErrors(account);
      }

      // Refresh account data to ensure sync
      await fetchAccount(user.accountId);

      toast(UiToastAdapter('Location notes updated successfully!', 'success'));
      
      // Close modal after successful update and notification
      onCloseNotesModal();
    } catch (error) {
      console.error('Error updating location notes:', error);
      toast(UiToastAdapter('Error updating location notes', 'error'));
      onCloseNotesModal();
    }
  };

  return (
    <>
      <Columns isMultiline>
        <Column isSize="1/2">
          <MuiTitle isSize="4" hasTextColor="info" tag="h2">
            Locations
          </MuiTitle>
        </Column>
        <Column isSize="1/2">
          <NavLink
            className="button is-gradient-bright is-long is-pulled-right"
            to="/locations/add"
          >
            <span className="icon is-left">
              <FontAwesomeIcon icon={['fal', 'plus']} />
            </span>
            <span>Add Location</span>
          </NavLink>
        </Column>
        <Column>
          <LocationsTable
            loading={loading}
            locations={account.locations || []}
            onDeleteLocation={onDeleteLocation}
            onViewNotes={onViewNotes}
          />
        </Column>
        {!user.isBankAdmin && !user.isSuperAdmin && (
          <Column isSize="narrow">
            <GuidelinesCard
              guidelines={
                user.accounts[0].financialInstitutionAccount.banking
                  ? guidelines
                  : guidelinesPayments
              }
            />
          </Column>
        )}
      </Columns>
      <ConfirmDeletionModal
        isActive={!!deletingLocation}
        onClickCancel={onCancelDeleteLocation}
        onClickConfirm={onConfirmDeleteLocation}
        title={`${(deletingLocation || {}).type} - ${
          (deletingLocation || {}).address
        }`}
      />
      <LocationNotesModal
        isActive={notesModalOpen}
        onCancel={onCloseNotesModal}
        onSubmit={onSubmitNotes}
        action="Save"
        initialNotes={selectedLocationForNotes?.location_notes || ''}
      />
    </>
  );
};

LocationsListPage.propTypes = {
  fetchAccount: propTypes.func.isRequired,
  setBreadcrumbs: propTypes.func.isRequired,
  deleteAccountLocation: propTypes.func.isRequired,
  updateAccountLocation: propTypes.func.isRequired,
  token: propTypes.object.isRequired,
  user: propTypes.object.isRequired,
  account: propTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    token: getToken(state),
    user: getUser(state),
    account: getAccount(state),
  };
}

const connectedLocatioinsListPage = connect(mapStateToProps, {
  fetchAccount,
  setBreadcrumbs,
  deleteAccountLocation,
  updateAccountLocation,
})(LocationsListPage);

export { connectedLocatioinsListPage as LocationsListPage };
